import {useCallback, useEffect, useRef, useState} from 'react';

import {DiscoveryUnit} from '@udemy/discovery-api';
import {
    fetchRecommendationsContext,
    RecommendationContextIds,
    RecommendationSourceObjectTypes,
} from '@udemy/react-recommendations';

import {
    CursorMap,
    extractCursors,
    fetchMoreTrackedItems,
    toTrackedDiscoveryUnits,
} from 'src/lib/recommendations/recommendation-unit-helpers';

interface UseTopicRecommendationsParams {
    topicId: string;
    pageType: string;
    // TODO: CVR-1878 - Remove when recommendation units migration is complete
    isEnabled?: boolean;
    requestedSectionCount?: number;
    requestedItemCountPerUnit?: number;
}

export type OnLoadMoreByTitle = (title: string) => Promise<Partial<DiscoveryUnit>>;

interface UseTopicRecommendationsResult {
    units: Partial<DiscoveryUnit>[];
    isLoading: boolean;
    error: unknown;
    onLoadMore: OnLoadMoreByTitle;
}

const DEFAULT_SECTION_COUNT = 7;
const DEFAULT_ITEM_COUNT = 12;

export function useTopicRecommendations({
    topicId,
    pageType,
    isEnabled = true,
    requestedSectionCount = DEFAULT_SECTION_COUNT,
    requestedItemCountPerUnit = DEFAULT_ITEM_COUNT,
}: UseTopicRecommendationsParams): UseTopicRecommendationsResult {
    const [units, setUnits] = useState<Partial<DiscoveryUnit>[]>([]);
    const [isLoading, setIsLoading] = useState(isEnabled);
    const [error, setError] = useState<unknown>(null);
    const cursors = useRef<CursorMap>(new Map());
    const hasFetched = useRef(false);

    useEffect(() => {
        if (!isEnabled || hasFetched.current) return;
        hasFetched.current = true;

        const fetchUnits = async () => {
            try {
                const {recommendationContext} = await fetchRecommendationsContext({
                    params: {
                        contextId: RecommendationContextIds.TOPIC,
                        sourceObjects: [{type: RecommendationSourceObjectTypes.LABEL, id: topicId}],
                        requestedSectionCount,
                        requestedItemCountPerUnit,
                    },
                });

                const sections = recommendationContext.sections ?? [];
                cursors.current = extractCursors(sections);
                setUnits(toTrackedDiscoveryUnits(sections, cursors.current));
            } catch (e) {
                setError(e);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUnits();
    }, [isEnabled, topicId, pageType, requestedSectionCount, requestedItemCountPerUnit]);

    const onLoadMore = useCallback(
        async (title: string): Promise<Partial<DiscoveryUnit>> => {
            const cursor = cursors.current.get(title);
            if (!cursor) {
                return {items: [], remaining_item_count: 0};
            }

            const {items, nextCursor} = await fetchMoreTrackedItems(
                cursor,
                requestedItemCountPerUnit,
            );

            cursors.current.set(title, nextCursor);

            return {
                items: items ?? [],
                remaining_item_count: nextCursor ? 1 : 0,
            };
        },
        [requestedItemCountPerUnit],
    );

    return {units, isLoading, error, onLoadMore};
}
